<template>
  <component
    :is="commerceMode || asvMode || isJustStreet ? 'div' : 'el-card'"
    v-loading="loading"
    :element-loading-text="loadingText"
    style="flex: 1"
  >
    <el-form :disabled="disabled" class="street-form" label-position="top" size="mini">
      <el-row :gutter="10">
        <el-col
          :span="isStreetModeAndIsNotPOI && !isCrossStreetMode ? (isJustStreet ? 21 : 18) : 24"
        >
          <div class="input-header">
            <label class="el-form-item__label py-0">{{ label }}</label>
            <div v-if="providedAddress" class="input-header-hint">
              {{ providedAddress }}
            </div>
            <div v-if="formData.deleted" class="deleted-title">
              <strong>Atención</strong>
              Calle eliminada
            </div>
            <el-button
              v-if="!asvMode && !isJustStreet"
              class="py-0"
              size="mini"
              type="text"
              @click.stop="toggleStreetMode"
            >
              <span v-if="isStreetMode">Buscar cruce</span>
              <span v-else>Buscar calle</span>
            </el-button>
            <el-button
              v-if="!asvMode && !isJustStreet && streetText?.length > 0"
              class="py-0"
              size="mini"
              type="text"
              @click.stop="handleOnlineStreetSearch"
            >
              <span>Buscar online</span>
            </el-button>
            <div class="input-header-right">
              <div v-if="deliveryInfo" class="service-address-details">
                {{ deliveryInfoResume }}
                <span class="material-icons m-icon-s pointer" @click="handleOpenDeliveryInfoDrawer">
                  info
                </span>
              </div>
            </div>
          </div>
          <el-form-item :class="formItemErrorClass(validations.street)" :prop="prop">
            <div class="street-fields">
              <street-input
                ref="street"
                v-model="formData.street"
                :streetText.sync="streetText"
                :workgroup-id="workgroupId"
                :central-id="centralId"
                @setStreet="handleSetStreet"
                @clearStreet="handleClearStreet"
              />
              <street-input
                v-if="isCrossStreetMode"
                ref="crossStreet"
                v-model="crossStreet"
                class="ml-2"
                :streetText.sync="crossStreetText"
                :workgroup-id="workgroupId"
                :central-id="centralId"
                placeholder="Introduce la calle con la que se cruza"
                @setStreet="handleCrossStreetChange"
              />
            </div>
            <error-message
              :data="validations.street"
              :mode="isJustStreet ? 'text' : 'icon'"
              validation="required"
            >
              Es obligatorio indicar una calle
            </error-message>
          </el-form-item>
        </el-col>
        <el-col v-if="isStreetModeAndIsNotPOI && !isCrossStreetMode" :span="3">
          <el-form-item :class="formItemErrorClass(validations.portal)" label="Portal">
            <div slot="label">
              Portal
              <div v-if="!portalExists" class="label-right-warning">no existe</div>
            </div>
            <portal-selector
              ref="portal"
              v-model="formData.portal"
              cache-key="StreetToCardPortalSelector"
              :class="{ 'input-warning': !portalExists }"
              :disabled="isPortalDisabled"
              :street="selectedStreet"
              :tabindex="getTabIndex('from.portal')"
              :workgroup-id="workgroupId"
            />
            <error-message
              slot="suffix"
              :data="validations.portal"
              mode="icon"
              validation="required"
            >
              Es obligatorio indicar un portal
            </error-message>
          </el-form-item>
        </el-col>
        <el-col v-if="isStreetModeAndIsNotPOI && !isCrossStreetMode && !isJustStreet" :span="3">
          <el-form-item label="Letra">
            <div slot="label">
              Letra
              <div v-if="!portalLetterExists" class="label-right-warning">no existe</div>
            </div>
            <portal-letter-selector
              ref="portalLetter"
              v-model="formData.letter"
              cache-key="StreetFromCardPortalLetterSelector"
              :class="{ 'input-warning': !portalLetterExists }"
              :disabled="isPortalDisabled"
              :portal="formData.portal"
              :street="selectedStreet"
              :tabindex="getTabIndex('from.letter')"
              :workgroup-id="workgroupId"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row v-if="!isJustStreet" :gutter="10" class="grow">
        <el-col v-if="!isEdit && !commerceMode" :span="12" class="grow">
          <div class="card-header">
            <div class="el-form-item__label">Últimas direcciones</div>
            <el-button
              v-if="showLastAddressesManagementButton && lastAddresses && lastAddresses.length > 0"
              class="manage-last-addresses-button"
              size="mini"
              type="text"
              @click="handleManageLastAddresses"
            >
              Gestionar
            </el-button>
          </div>
          <div v-if="lastAddresses && lastAddresses.length === 0" class="last-addresses empty">
            No hay últimas direcciones
          </div>
          <div v-else class="last-addresses">
            <div
              v-for="lastAddress in lastAddresses"
              :key="`${lastAddress.streetId}-${lastAddress.streetNumber}`"
              class="last-address"
            >
              <div class="text" @click="handleClickLastAddress(lastAddress)">
                {{ lastAddress.text }}
              </div>
            </div>
          </div>
        </el-col>
        <el-col
          v-if="!asvMode || isJustStreet"
          :span="isEdit || commerceMode ? 24 : 12"
          class="grow"
        >
          <el-row style="margin-bottom: 0; padding-bottom: 0" :gutter="10">
            <el-col :span="12">
              <el-form-item label="Nombre pasajero">
                <el-input
                  v-model="formData.passengerName"
                  :tabindex="getTabIndex('from.passenger')"
                  :placeholder="isCustomerSet ? customer.name : 'Nombre pasajero'"
                  size="mini"
                />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="Aclaración entrega">
                <el-input
                  v-model="formData.info"
                  :tabindex="getTabIndex('from.hint')"
                  placeholder="Aclaración para la entrega del despacho"
                  size="mini"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <div class="card-header notes-header">
            <div class="el-form-item__label">Notas de la recogida</div>
            <el-button
              v-if="!disabled && !disabledSaveNumberNotes && !isCustomerSet"
              class="save-portal-notes"
              size="mini"
              type="text"
              @click="saveNumberNotes"
            >
              Guardar notas en el portal
            </el-button>
            <el-popover
              v-if="!disabled && !disabledSaveNumberNotes && isCustomerSet"
              ref="saveAddressPopover"
            >
              <el-button class="save-on-portal" size="mini" type="primary" @click="saveNumberNotes">
                <span class="material-icons m-icon-s">place</span>
                Guardar notas en el portal
              </el-button>
              <el-button
                class="save-on-phone"
                size="mini"
                type="primary"
                @click="saveNumberNotesOnCustomer"
              >
                <span class="material-icons m-icon-s">phone</span>
                Guardar notas en el teléfono
              </el-button>
              <el-button slot="reference" class="save-portal-notes" size="mini" type="text">
                Guardar notas</el-button
              >
            </el-popover>
          </div>
          <el-popover v-model="notesUpdatePopoverFlag" placement="top" trigger="manual">
            <div>
              <div class="notes-title">{{ notesTitle }}</div>
              <div class="notes-text">
                {{ notesToBeSet }}
              </div>
            </div>
            <div class="notes-buttons">
              <div class="notes-question">¿Qué quieres hacer?</div>
              <el-button class="ml-3" size="mini" type="info" @click="updateNotes('ignore')"
                >Ignorar</el-button
              >
              <el-button
                v-if="!formData.notes"
                size="mini"
                type="primary"
                @click="updateNotes('override')"
              >
                Añadir
              </el-button>
              <el-button
                v-if="formData.notes"
                size="mini"
                type="primary"
                @click="updateNotes('concat')"
              >
                Añadir al final
              </el-button>
              <el-button
                v-if="formData.notes"
                size="mini"
                type="warning"
                @click="updateNotes('override')"
              >
                Reemplazar
              </el-button>
            </div>
            <el-input
              ref="notes"
              slot="reference"
              v-model="formData.notes"
              :disabled="disabled"
              :rows="2"
              :tabindex="getTabIndex('from.notes')"
              class="notes"
              placeholder="Notas de la dirección de recogida"
              size="mini"
              type="textarea"
              @focus="handleNotesFocus"
              @blur="handleNotesBlur"
            />
          </el-popover>
        </el-col>
      </el-row>
    </el-form>
    <delivery-info-modal-drawer
      :delivery-info="deliveryInfo"
      :show="deliveryInfoDrawerFlag"
      :street-from="selectedStreet"
      :street-to="selectedCrossStreet"
      @changeShow="deliveryInfoDrawerFlag = $event"
    />
    <OnlineStreetSearchDrawer
      :wgId="workgroupId"
      :centralId="centralId"
      :initial-search="initialOnlineStreetSearchText"
      :show="onlineStreetSearchDrawerFlag"
      @created="handleCreated"
      @changeShow="onlineStreetSearchDrawerFlag = $event"
    />
  </component>
</template>

<script>
import { off, on } from "element-ui/src/utils/dom";
import { mapActions, mapGetters } from "vuex";

import ErrorMessage from "@/components/common/forms/ErrorMessage";
import DeliveryInfoModalDrawer from "@/components/Services/Shared/DeliveryInfoModalDrawer";
import PortalLetterSelector from "@/components/Services/Shared/PortalLetterSelector";
import PortalSelector from "@/components/Services/Shared/PortalSelector";
import cache from "@/mixins/cache";
import errors from "@/mixins/errors";
import tabindex from "@/mixins/services/tabindex";
import { equals } from "@/utils/arrays";
import { closestFromArrays } from "@/utils/coords";
import { valueOrNull } from "@/utils/data";
import OnlineStreetSearchDrawer from "./Shared/OnlineStreetSearchDrawer.vue";
import StreetInput from "./Shared/StreetInput.vue";

const defaultData = () => ({
  streetText: "",
  crossStreetText: "",
  onlineStreetSearchDrawerFlag: false,
  initialOnlineStreetSearchText: "",
  persistedCache: true,
  loading: false,
  loadingText: null,
  loadingPortal: false,
  loadingStreetText: false,
  deliveryInfo: null,
  selectedStreet: null,
  notesUpdatePopoverFlag: false,
  notesToBeSet: null,
  notesTitle: null,
  showPortalPopover: false,
  cachedNearestPortals: [],
  askForUpdates: false,
  deliveryInfoDrawerFlag: false,
  mode: "STREET",
  crossStreet: null,
  selectedCrossStreet: null,
  timeout: null,
  alreadyWarnAboutRecentService: false,
});

export default {
  name: "StreetFromCard",

  components: {
    DeliveryInfoModalDrawer,
    ErrorMessage,
    PortalSelector,
    PortalLetterSelector,
    StreetInput,
    OnlineStreetSearchDrawer,
  },

  mixins: [errors, cache, tabindex],

  props: {
    label: {
      type: String,
      default: "Recogida",
    },
    prop: String,
    value: Object,
    customer: Object,
    validations: Object,
    lastAddresses: Array,
    workgroupId: Number,
    centralId: Number,
    isEdit: Boolean,
    providedAddress: String,
    commerceMode: Boolean,
    isJustStreet: Boolean,
    asvMode: Boolean,
    disabled: Boolean,
    checkStreetWithRecentServices: Boolean,
    showLastAddressesManagementButton: Boolean,
  },

  computed: {
    ...mapGetters("home", ["tripsPending", "tripsDelivered"]),
    ...mapGetters("clientStreets", ["streets"]),
    formData() {
      return this.value;
    },
    completeStreet() {
      return this.selectedStreet && this.formData.portal;
    },
    portalExists() {
      if (this.formData.portal === null || this.selectedStreet === null) {
        return true;
      }
      return (
        this.selectedStreet.portals.filter(
          (p) => parseInt(p.number) === parseInt(this.formData.portal)
        ).length > 0
      );
    },
    portalLetterExists() {
      if (this.formData.portal === null || this.selectedStreet === null) {
        return true;
      }
      if (!this.portalExists) {
        return true;
      }
      return (
        this.selectedStreet.portals.filter(
          (p) => p.number === this.formData.portal && valueOrNull(p.letter) === this.formData.letter
        ).length > 0
      );
    },
    isPortalDisabled() {
      return !this.formData.street;
    },
    disabledSaveNumberNotes() {
      return (
        this.isPortalDisabled ||
        !this.portalExists ||
        !this.portalLetterExists ||
        this.formData.portal === null
      );
    },
    isCustomerSet() {
      return !!this.customer?.id;
    },
    isStreetModeAndIsNotPOI() {
      return (
        (this.isStreetMode && !this.selectedStreet) ||
        this.selectedStreet?.poi === 0 ||
        this.selectedStreet?.poi === false
      );
    },
    isStreetMode() {
      return this.mode === "STREET";
    },
    isCrossStreetMode() {
      return this.mode === "CROSS_STREET";
    },
    deliveryInfoResume() {
      const info = this.deliveryInfo;
      const parts = info.deliveryText
        ?.split(";")
        .slice(0, 2)
        .filter((a) => a[0]?.startsWith("P") || a[0]?.startsWith("Z"))
        .join(",");
      let resume = "";
      if (info.zoneRef) {
        resume += `${info.zoneRef}`;
      }
      if (info.zoneName) {
        resume += ` - ${info.zoneName}`;
      }
      if (resume) {
        resume += " / ";
      }
      if (info.nameDelivery) {
        resume += info.nameDelivery;
      }
      if (parts) {
        resume += ` - ${parts}`;
      }
      return resume;
    },
  },

  watch: {
    formData: {
      handler() {
        this.$emit("input", this.formData);
        this.checkRecentServices();
      },
      deep: true,
    },
    "formData.portal"() {
      this.handlePortalChange();
    },
    "formData.letter"() {
      this.handlePortalChange();
    },
    "formData.street"() {
      this.handlePortalChange();
    },
    deliveryInfo: {
      handler() {
        this.$emit("update:deliveryName", this.deliveryInfo?.nameDelivery);
      },
      deep: true,
    },
  },

  data() {
    return defaultData();
  },

  created() {
    on(document, "click", this.handleDocumentClick);
    this.askForUpdates = false;
    this.notesUpdatePopoverFlag = false;
    this.timeout = setTimeout(() => {
      this.askForUpdates = true;
    }, 2000);
  },

  destroyed() {
    off(document, "click", this.handleDocumentClick);
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  },

  methods: {
    ...mapActions("clientCustomers", ["updateCustomerPatch"]),
    ...mapActions("clientStreets", [
      "getStreets",
      "getStreet",
      "getStreetsNearestPortal3",
      "updatePortalStreet",
    ]),

    handleNotesFocus() {
      this.$refs.notes.$el.querySelector("textarea").style.height = "130px";
      this.$refs.notes.$el.querySelector("textarea").style.width = "120%";
    },

    handleNotesBlur() {
      this.$refs.notes.$el.querySelector("textarea").style.height = "auto";
      this.$refs.notes.$el.querySelector("textarea").style.width = "100%";
    },

    handleOnlineStreetSearch() {
      this.onlineStreetSearchDrawerFlag = true;
      this.initialOnlineStreetSearchText = this.streetText;
    },

    async handleCreated(id, number) {
      this.loading = true;
      this.loadingText = "Creando calle...";
      this.onlineStreetSearchDrawerFlag = false;

      if (this.$refs.street) {
        await this.$refs.street.reloadStreets();
        await this.$refs.street.handleSelect({ id });
      }

      if (number) {
        this.$emit("setPortal", number);
      }
      this.loading = false;
    },

    toggleStreetMode() {
      this.mode = this.mode === "STREET" ? "CROSS_STREET" : "STREET";
      if (this.mode !== "CROSS_STREET") this.clearCrossStreet();
    },

    clearCrossStreet() {
      this.selectedCrossStreet = null;
      this.crossStreet = null;
      this.$refs.crossStreet.handleClear();
    },

    handleReset() {
      this.deliveryInfo = null;
    },

    handleClear() {
      Object.assign(this.$data, defaultData());
      this.$refs.street.handleClear();
      if (this.$refs.crossStreet) {
        this.$refs.crossStreet.handleClear();
      }
    },

    handleSetPortal(number) {
      this.formData.portal = number;
      this.formData.letter = null;
    },

    checkRecentServices() {
      if (this.completeStreet && !this.alreadyWarnAboutRecentService) {
        const hint = this.selectedStreet.hint ? ` (${this.selectedStreet.hint})` : "";
        const streetTextToCheck = `${this.selectedStreet.name}${hint}, ${this.formData.portal}`;

        let recent = this.tripsPending.find((p) => p.service.streetFrom.text === streetTextToCheck);
        if (recent) {
          this.alreadyWarnAboutRecentService = true;
          this.$confirm("Ya hay un servicio en reparto para esta dirección", {
            title: "Servicio reciente",
            type: "warning",
            showCancelButton: false,
          });
          return;
        }

        recent = this.tripsDelivered.find((p) => p.service.streetFrom.text === hint);
        if (recent) {
          this.alreadyWarnAboutRecentService = true;
          this.$confirm("Ya hay un servicio en curso para esta dirección", {
            title: "Servicio reciente",
            type: "warning",
            showCancelButton: false,
          });
        }
      }
    },

    handleClearStreet() {
      this.$emit("clearStreet");
    },

    handleSetStreet(street, manual) {
      if (street && (!street.portals || street.portals.length < 1)) {
        this.$message.error(
          "La calle seleccionada no tiene portales para enviar el servicio, seleccione otra."
        );
        this.$refs.street.handleClear();
        return;
      }
      this.selectedStreet = street;
      this.formData.deleted = street?.deleted || null;
      this.$emit("selectedStreet", street);
      if (street) {
        this.$emit("changeCentral", street.idCentral);
        if (manual) {
          if (street.poi) {
            this.formData.portal = 0;
            this.formData.letter = null;
          }
          setTimeout(() => {
            if (this.$refs.portal) {
              this.$refs.portal.$el.querySelector("input").focus();
            }
          }, 100);
        }
      } else {
        this.formData.portal = null;
        this.formData.letter = null;
      }
      this.$emit("input", this.formData);
    },

    addCrossStreetToNotes(street) {
      this.cleanUpCrossStreeNotes();
      const lines = this.formData.notes.split("\n");
      lines.push(`Cruze de ${this.selectedStreet.name} con ${street.name}`);
      this.formData.notes = lines.filter(Boolean).join("\n");
    },

    handleCrossStreetChange(street) {
      if (street) {
        this.selectedCrossStreet = street;
        const originPortals = this.selectedStreet.portals.map((p) => p.coords);
        const crossPortals = street.portals.map((p) => p.coords);
        const nearest = closestFromArrays(originPortals, crossPortals, 200);
        let nearestPortal = {};

        if (!nearest) {
          this.$message({
            type: "error",
            message:
              "Las calles que has introducido no se cruzan o no hay portales coincidentes a menos de 50 metros",
          });
          this.clearCrossStreet();
          this.$refs.crossStreet.$el.querySelector("input").focus();
        } else {
          nearestPortal = this.selectedStreet.portals.find((p) => equals(p.coords, nearest));
        }

        this.formData.portal = nearestPortal?.number >= 0 ? nearestPortal?.number : null;
        this.formData.letter = nearestPortal?.letter || null;
        this.handlePortalChange();
        const portalAndLetter = this.formData.letter
          ? `${this.formData.portal}-${this.formData.letter}`
          : this.formData.portal;
        this.$refs.street.appendToStreetText(`, ${portalAndLetter}`);

        this.addCrossStreetToNotes(street);
      }
    },

    handleOpenDeliveryInfoDrawer() {
      this.deliveryInfoDrawerFlag = true;
    },

    async handleClickLastAddress(address) {
      this.formData.street = address.streetId;
      this.formData.portal = parseInt(address.streetNumber);
      this.formData.letter = address.letter || null;
      this.$emit("changeCentral", address.idCentral);

      if (address.lastAddressAssociatedOriginNote) {
        this.askForUpdateNotes(
          "La dirección seleccionada tiene notas asociadas al número de teléfono",
          address.lastAddressAssociatedOriginNote
        );
      }

      this.checkRecentServices();

      setTimeout(() => {
        this.$refs.street.fillStreetText();
      }, 10);

      setTimeout(() => {
        let lastIndex = this.$refs.street?.tabindex;
        if (this.$refs.portal?.tabindex >= lastIndex) {
          lastIndex = this.$refs.portal?.tabindex;
        }
        if (this.$refs.portalLetter?.tabindex >= lastIndex) {
          lastIndex = this.$refs.portalLetter?.tabindex;
        }
        document.querySelector(`[tabindex="${parseInt(lastIndex) + 1}"]`)?.focus();
      }, 200);
    },

    askForUpdateNotes(title, notes) {
      if (this.askForUpdates) {
        this.notesTitle = title;
        this.notesToBeSet = notes;
        this.notesUpdatePopoverFlag = true;
      }
    },

    updateNotes(method) {
      switch (method) {
        case "override":
          this.formData.notes = this.notesToBeSet;
          break;
        case "concat":
          this.formData.notes = `${this.formData.notes}\n${this.notesToBeSet}`;
          break;
        case "ignore":
      }
      this.notesToBeSet = null;
      this.notesUpdatePopoverFlag = false;
      this.$refs.notes.focus();
    },

    cleanUpCrossStreeNotes() {
      if (!this.formData.notes) return;
      const crossIndex = this.formData.notes.split("\n").findIndex((l) => l.startsWith("Cruze de"));
      if (crossIndex !== false) {
        this.formData.notes = this.formData.notes
          .split("\n")
          .filter((_, i) => i !== crossIndex)
          .join("\n");
      }
    },

    async handlePortalChange() {
      const street = this.formData.street;
      const portal = this.formData.portal;
      const letter = this.formData.letter || null;
      const workgroupId = this.workgroupId;

      if (!this.isCrossStreetMode) {
        this.cleanUpCrossStreeNotes();
      }

      if (street && portal !== null) {
        let deliveryInfo = this.cachedNearestPortals?.find(
          (p) => p.number === parseInt(portal) && p.letter === letter
        );
        if (!deliveryInfo) {
          const data = await this.getStreetsNearestPortal3({
            wgId: workgroupId,
            streetId: street,
            portalNumber: portal,
            letter,
          });
          if (data) {
            this.cachedNearestPortals.push(...data);
            deliveryInfo = data[0];
          }
        }
        if (deliveryInfo) {
          this.$emit("changeAdvance", deliveryInfo.defaultAdvanceBookTrip);
          this.deliveryInfo = deliveryInfo;
          this.$emit("update:coords", this.deliveryInfo.coords);
          if (deliveryInfo.number === portal && deliveryInfo.letter === letter) {
            const numberNotes = deliveryInfo.data?.numberNotes;

            if (numberNotes && !this.notesUpdatePopoverFlag) {
              this.askForUpdateNotes("El portal seleccionado tiene notas asociadas", numberNotes);
            }
            this.$emit("setRequirements", [...(deliveryInfo.requirements || [])]);
          }
        }

        if (!this.formData.letter && this.$refs.portalLetter && this.askForUpdates) {
          let lastIndex = this.$refs.portal?.tabindex;
          document.querySelector(`[tabindex="${parseInt(lastIndex) + 1}"]`)?.focus();
        }
      } else {
        this.deliveryInfo = null;
      }
      this.$forceUpdate();
    },

    focusStreet() {
      this.$refs.street.focus();
    },

    handleManageLastAddresses() {
      this.$emit("manageLastAddresses");
    },

    async saveNumberNotes() {
      const { notes } = this.formData;
      await this.updatePortalStreet({
        params: {
          wgId: this.workgroupId,
          streetId: this.formData.street,
          portalId: this.deliveryInfo.id,
        },
        data: {
          data: {
            numberNotes: notes,
          },
        },
      });
      if (this.$refs.saveAddressPopover) {
        this.$refs.saveAddressPopover.doClose();
      }
      // Update notes on delivery info since we're caching them
      this.deliveryInfo.data = {
        ...this.deliveryInfo.data,
        numberNotes: notes,
      };
      this.$message({
        type: "success",
        message: "Las notas se han guardado en el portal correctamente",
      });
    },

    async saveNumberNotesOnCustomer() {
      const { notes } = this.formData;
      const lastAddresses = JSON.parse(JSON.stringify(this.lastAddresses));
      const index = lastAddresses.findIndex(
        (a) =>
          a.streetId === this.formData.street &&
          a.streetNumber === this.formData.portal &&
          a.idCentral === this.centralId
      );
      if (index >= 0) {
        lastAddresses[index].lastAddressAssociatedOriginNote = notes;
      } else {
        lastAddresses.unshift({
          streetId: this.formData.street,
          streetNumber: this.formData.portal,
          freq: 0,
          last: Date.now() / 1000,
          text: `${this.formData.streetSearch} ${this.formData.portal}`,
          idCentral: this.centralId,
          lastAddressAssociatedOriginNote: notes,
        });
      }
      const payload = {
        data: {
          lastAddress: lastAddresses,
        },
        params: {
          phoneId: this.customer.id,
          wgId: this.workgroupId,
        },
      };
      await this.updateCustomerPatch(payload);
      if (this.$refs.saveAddressPopover) {
        this.$refs.saveAddressPopover.doClose();
      }
      this.$message({
        type: "success",
        message: "Las notas se han guardado en el teléfono correctamente",
      });
    },

    prependToHint(text, replaceRegex) {
      this.prependToField(text, replaceRegex, "info");
    },

    prependToNote(text, replaceRegex) {
      this.prependToField(text, replaceRegex, "notes");
    },

    prependToField(text, replaceRegex, field) {
      if (replaceRegex && this.formData[field].match(replaceRegex)) {
        this.formData[field] = this.formData[field].replace(replaceRegex, text);
      } else {
        let updated = text;
        if (this.formData[field]) {
          updated += `\n${this.formData[field]}`;
        }
        this.formData[field] = updated;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.street-form {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.grow {
  flex: 1;
  height: 100%;
}

.el-autocomplete {
  width: 100%;
}

.last-addresses {
  display: block;
  overflow-y: auto;
  border: 1px solid #ddd;
  height: 100%;
  max-height: 108px !important;
  width: 100%;
  border-radius: 4px;
  font-size: 12px;
  color: #555;

  &.empty {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .last-address {
    height: 24px;
    align-items: center;
    padding: 0 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    border-bottom: 1px solid #eee;
    width: 100%;
    display: flex;

    .text {
      flex: 1;
      line-height: 24px;
    }
  }
}

.manage-last-addresses-button {
  margin-top: -4px;
  padding: 2px 4px;
}

.card-header {
  display: flex;
  justify-content: space-between;
}

.notes-header {
  margin-top: 0;
}

.service-address-details {
  font-size: 10px;

  .m-icon {
    vertical-align: -2px;
  }
}

.save-portal-notes {
  margin-top: -4px;
  padding: 2px 4px;
}

div.el-form-item__label {
  font-weight: bold;
}

::v-deep .input-warning input {
  background: #fff0bd !important;
  border: 1px solid #dba800 !important;
}

::v-deep .el-input__suffix .el-spinner-inner {
  margin-top: 4px;

  .path {
    stroke: #777;
  }
}

.save-on-portal {
  background-color: rgb(179, 101, 0);
  border-color: rgb(179, 101, 0);
}

.save-on-phone {
  background-color: rgb(114, 4, 218);
  border-color: rgb(114, 4, 218);
}

.notes-text {
  background-color: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  padding: 10px;
  margin: 10px 0 15px;
  font-size: 12px;
}

.notes-buttons {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.notes-title {
  font-weight: bold;
  font-size: 13px;
  margin: 4px 0 8px;
}

.notes {
  z-index: 10;
  position: relative;
}

.notes-question {
  font-weight: bold;
  font-size: 12px;
  margin-right: auto;
}

.portal-doesnt-match {
  text-decoration: line-through;
  display: inline-block;
}

.dropdown-portals-button {
  position: absolute;
  z-index: 100;
  color: #444;
  right: 6px;
  top: 1px;
}

.dropdown-portals {
  max-height: 200px;
  overflow-y: auto;
}

.dropdown-portal-item {
  width: 100%;
  text-align: center;
  color: #444;

  &:hover,
  &.active {
    color: $menuActiveText;
    font-weight: bold;
  }
}

.label-right-warning {
  position: absolute;
  right: 0;
  top: 0;
  color: #dba800;
  font-weight: normal;
  font-style: italic;
}

::v-deep .el-form-item {
  position: relative;
}

.street-fields {
  display: flex;
}

.input-header {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 2px;
  position: relative;
}

.deleted-title {
  color: red;
  font-size: 12px;
}

.input-header-hint {
  font-style: italic;
  font-size: 10px;
  color: #999;
}

.input-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
}
</style>
